import { useInView } from 'framer-motion';
import type { LottieRefCurrentProps } from 'lottie-react';
import { useLocale } from 'next-intl';
import { lazy, useEffect, useMemo, useRef, useState } from 'react';

import type { AppUIImageProps } from './AppUIImage';
import AppUIImage from './AppUIImage';
import useLottieData from './hooks/useLottieData';

const Lottie = lazy(() => import('lottie-react'));

export interface AppUILottieProps {
  readonly dimensionsByLocale?:
    | Record<
        string,
        {
          height: number;
          width: number;
        }
      >
    | undefined;
  readonly fallbackImage: Omit<AppUIImageProps, 'dimensionsByLocale'>;
  readonly height?: number;
  readonly path: string;
  readonly shouldLoop?: boolean;
  readonly shouldPlay?: boolean;
  readonly width?: number;
}

const AppUILottie = ({
  dimensionsByLocale,
  fallbackImage,
  path,
  shouldLoop,
  shouldPlay,
  ...props
}: AppUILottieProps): JSX.Element | null => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(wrapperRef);
  const { animationData } = useLottieData({ path });
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const locale = useLocale();

  const handleDOMLoaded = () => {
    setIsLoaded(true);
  };

  useEffect(() => {
    if (isLoaded && isInView && shouldPlay) {
      lottieRef.current?.goToAndStop(0);
      setTimeout(() => {
        lottieRef.current?.play();
      }, 500);
    }
  }, [isInView, isLoaded, shouldPlay]);

  const dimensions = useMemo(
    (): { height: number | undefined; width: number | undefined } => ({
      height: props.height,
      width: props.width,
      ...dimensionsByLocale?.[locale],
    }),
    [dimensionsByLocale, locale, props],
  );

  return (
    <div
      ref={wrapperRef}
      className="flex w-full items-center"
      style={{ aspectRatio: `${dimensions.width}/${dimensions.height}` }}
    >
      {animationData && (
        <Lottie
          animationData={animationData}
          autoplay={false}
          data-cy="app_ui_lottie"
          loop={Boolean(shouldLoop)}
          lottieRef={lottieRef}
          onDOMLoaded={handleDOMLoaded}
        />
      )}
      <noscript>
        <AppUIImage
          {...fallbackImage}
          {...props}
          dimensionsByLocale={dimensionsByLocale}
        />
      </noscript>
    </div>
  );
};

export default AppUILottie;
